import {
  buildPath,
  buildTemplatePath,
  CloseButton,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { BulkSelected, SubmitButton } from "components";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  StudentGroup,
  StudentGroupBulkUpdate,
  StudentGroupBulkUpdateSchema,
  StudentGroupEditResource,
} from "generated/openApi";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { GroupUpdateSchema } from "./GroupUpdateSchema";

const routeName = "studentGroupBulkUpdate";

export const studentGroupBulkUpdatePageRoute: PageRoute = {
  handle: "Batch Update Groups",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type StudentGroupBulkUpdatePageState = {
  selectedIds: number[];
};

export const StudentGroupBulkUpdatePage = () => {
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<StudentGroupBulkUpdatePageState>(state);
  const [bulkUpdate, setBulkUpdate] = useState<StudentGroupBulkUpdate>({} as StudentGroupBulkUpdate);
  const [groups, setGroups] = useState<Map<number, StudentGroup>>(new Map());
  const [resource, setResource] = useState<StudentGroupEditResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkUpdate] });

  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getBulkUpdate(stateParam.selectedIds);
    fetchResource();
  }, []);

  const getBulkUpdate = (ids: number[]) => {
    ApiService.studentGroups.getBulkUpdate(ids).then((response) => {
      setBulkUpdate(response);
      setGroups(new Map(response.groups?.map((x) => [x.id, x])));
      resetUnsavedChanges([response]);
    });
  };

  const fetchResource = () => {
    ApiService.studentGroups.getBulkUpdateResource().then((response) => {
      setResource(response);
    });
  };

  const save = (bulkUpdate: StudentGroupBulkUpdate) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    ApiService.studentGroups
      .saveBulkUpdate({ ...bulkUpdate, groups: Array.from(groups.values()) })
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          dispatch(setAppToast(newToast("error")));
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
        } else {
          dispatch(setAppToast(newToast("success")));
          resetUnsavedChanges([bulkUpdate]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="inherit">{studentGroupBulkUpdatePageRoute.handle}</Typography>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />

            <SubmitButton
              onClick={() => save(bulkUpdate)}
              disabled={!(bulkUpdate?.groups && bulkUpdate?.groups?.length > 0 && unsavedChanges)}
            />
          </Stack>
        </Stack>
      }
    >
      <Stack spacing="md">
        <GroupUpdateSchema
          updateSchema={bulkUpdate.updateSchema || ({} as StudentGroupBulkUpdateSchema)}
          setUpdateSchema={(value) => setBulkUpdate({ ...bulkUpdate, updateSchema: value })}
          resource={resource}
          validationErrors={validationErrors?.[pn<StudentGroupBulkUpdate>("updateSchema")] || []}
        />

        <BulkSelected
          label="Group"
          items={groups || []}
          setItems={(value) => setGroups(value)}
          getItemLabel={(group) => (
            <Stack>
              <Typography>{group.name}</Typography>
              <Typography variant="caption" lineHeight="100%">
                {group.school?.name}
              </Typography>
            </Stack>
          )}
          errors={selectedItemsErrors}
          setErrors={setSelectedItemsErrors}
        />
      </Stack>
    </Page>
  );
};
